import { CloseOutlined } from "@mui/icons-material";
import { Box, SwipeableDrawer } from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import type { TransactionDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { getPaymentLinkDescriptor } from "Utils/BankUtils";
import { TransactionQRCode } from "Components/Merchant/InitiatePaymentDetail/shared/TransactionQRCode";
import { TransactionInformation } from "Components/Merchant/InitiatePaymentDetail/shared/TransactionInformation";

export type DrawerState = "closed" | "qrCode" | "info";

type Props = {
  drawerState: DrawerState;
  onOpen: () => void;
  onClose: () => void;
  transactionsDetail: TransactionDetailDto;
  paypointName?: string | null;
};

export const ContentDrawer = ({
  drawerState,
  onOpen,
  onClose,
  paypointName,
  transactionsDetail,
}: Props) => {
  const { t } = useResource();

  const drawerStateToContent: Record<DrawerState, React.ReactNode> = {
    qrCode: (
      <TransactionQRCode
        transactionStatus={transactionsDetail.transactionStatus}
        paymentDescriptor={getPaymentLinkDescriptor(
          transactionsDetail.publicID,
        )}
      />
    ),
    info: (
      <TransactionInformation
        paypointName={paypointName}
        transactionsDetail={transactionsDetail}
      />
    ),
    closed: undefined,
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={drawerState !== "closed"}
      onClose={onClose}
      onOpen={onOpen}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          p: 3,
          maxHeight: "85vh",
        },
      }}
      disableDiscovery
      // Modal level, not a hardcoded 10001 — see ResponsiveDialog: a sheet above that
      // level paints over the dropdowns its own content portals to <body>.
      sx={{ zIndex: ({ zIndex }) => zIndex.modal }}
    >
      <Box display="flex" justifyContent="center" mb={6}>
        <Box
          aria-hidden
          sx={{ width: 80, height: 6, bgcolor: "grey.300", borderRadius: 2 }}
        />
        <UnstyledButton
          aria-label={t(Resources.Common.Back)}
          onClick={onClose}
          sx={{
            position: "absolute",
            color: "grey.600",
            right: 12,
            top: 12,
          }}
        >
          <CloseOutlined />
        </UnstyledButton>
      </Box>
      <Box maxWidth={["100%", "fit-content"]} mx={[2, "auto"]} mb={3}>
        {drawerStateToContent[drawerState]}
      </Box>
    </SwipeableDrawer>
  );
};
